// @mui material components
import Grid from '@mui/material/Grid';

// Material Dashboard 2 PRO React TS components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import Icon from '@mui/material/Icon';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';

// Material Dashboard 2 PRO React TS examples components
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import ComplexStatisticsCard from 'examples/Cards/StatisticsCards/ComplexStatisticsCard';

import { useEffect, useState } from 'react';
import { Dispatch } from 'redux';
import moment from 'moment';
import {
	LoadingDispatchTypes,
	LOADING_END,
	START_LOADING,
} from 'actions/Loading/loadingTypes';
import { axiosClient, getToken } from 'Config/axiosConfig';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { RootStore } from 'store/Store';
import PXLoader from 'components/PXLoader/PXLoader';
import DefaultLineChart from 'examples/Charts/LineCharts/DefaultLineChart';
import MDBadgeDot from 'components/MDBadgeDot';
import PxcPieChart from './components/pxc-PieChart/PxcPieChart';
import { numeroAMoneda } from 'helpers/aMoneda';
import { numeroAMes } from 'helpers/aMes';

function Analytics(): JSX.Element {
	const dispatch = useDispatch();
	const { loading } = useSelector((state: RootStore) => state.loading);

	interface iPlan {
		timbres: number;
		vigencia: string;
		plan_id?: number;
		plan_nombre?: string;
	}
	const [suscripcionInfo, setSuscripcionInfo] = useState<iPlan>({
		timbres: 0,
		vigencia: '',
		plan_id: 0,
		plan_nombre: '',
	});
	const [pendienteCobro, setPendienteCobro] = useState<number>(0);
	const [ingresosMesActual, setIngresosMesActual] = useState({
		total: 0,
		mes: 0,
	});

	const currentYear = moment().format('yyyy');
	const lastYear = moment().subtract(1, 'year').format('yyyy');
	const currentMonth = moment().format('M');

	const [ingresosAnteriores, setIngresosAnteriores] = useState([]);
	const [ingresosActuales, setIngresosActuales] = useState([]);

	// KPIs adicionales (opcional: se poblan si el API expone /dashboard/kpis)
	interface DashboardKpis {
		ingresos_mtd?: number;
		variacion_mtd?: number; // porcentaje vs MTD-1
		tasa_cobro?: number; // porcentaje 0-100 o 0-1
		ticket_promedio?: number;
		facturas_mtd?: number;
		vencidas_count?: number;
		vencidas_total?: number;
		subtotal_mtd?: number;
		iva_mtd?: number;
		csd_days_to_expire?: number | null;
		csd_valid?: boolean | null;
		csd_expiring?: boolean | null;
		csd_expiration_date?: string | null;
		cancel_en_proceso_count?: number;
	}
	const [kpis, setKpis] = useState<DashboardKpis | null>(null);
	const [queryDoc, setQueryDoc] = useState('');

	const formatPct = (v?: number) => {
		if (v === undefined || v === null) return '--';
		const val = v <= 1 ? v * 100 : v;
		return `${val.toFixed(1)}%`;
	};

	useEffect(() => {
		//Obtener Suscripcion info
		const getSuscripcion = () => {
			return async (dispatch: Dispatch<LoadingDispatchTypes>) => {
				await getToken();
				dispatch({ type: START_LOADING });
				axiosClient
					.get(`/suscripcion`)
					.then((respuesta) => {
						if (respuesta.status === 200) {
							setSuscripcionInfo({
								timbres: respuesta.data.data.timbres_disponibles,
								vigencia: moment(respuesta.data.data.fecha_fin).format('DD/MM/YYYY'),
								plan_id: respuesta.data.data.plan_id,
								plan_nombre: respuesta.data.data.plan_nombre,
							});
						}
						dispatch({ type: LOADING_END });
					})
					.catch((error) => {
						if (error.response.status === 500) {
							console.log('error500', error.response.data);
							dispatch({ type: LOADING_END });
						} else if (error.response.status === 400) {
							console.log('error400', error.response.data);
							dispatch({ type: LOADING_END });
						} else if (error.response.status === 403) {
							console.log('error403', error.response.data);
							dispatch({ type: LOADING_END });
						} else if (error.response.status === 404) {
							console.log('error404', error.response.data);
							dispatch({ type: LOADING_END });
						} else {
							console.log(error.response.data);
							dispatch({ type: LOADING_END });
						}
					});
			};
		};
		dispatch<any>(getSuscripcion());

		// KPIs opcionales del dashboard (no bloquea si no existe el endpoint)
		const getDashboardKpis = () => {
			return async (dispatch: Dispatch<LoadingDispatchTypes>) => {
				await getToken();
				// Mostrar cache inmediato si existe para evitar flicker
				try {
					const cached = localStorage.getItem('pxcDashboardKpis');
					if (cached) setKpis(JSON.parse(cached));
				} catch {}
				axiosClient
					.get(`/dashboard/kpis`)
					.then((resp) => {
						setKpis(resp.data);
						try {
							localStorage.setItem('pxcDashboardKpis', JSON.stringify(resp.data));
						} catch {}
					})
					.catch(() => {
						// Silenciar si no existe el endpoint aún
					});
			};
		};
		dispatch<any>(getDashboardKpis());
		//Obtener pendienteCobro info
		const getPendienteCobro = () => {
			return async (dispatch: Dispatch<LoadingDispatchTypes>) => {
				await getToken();
				dispatch({ type: START_LOADING });
				axiosClient
					.get(`/comprobantes/pendienteCobro/`)
					.then((respuesta) => {
						if (respuesta.status === 200) {
							if (respuesta.data[0].total) {
								setPendienteCobro(respuesta.data[0].total);
							} else {
								setPendienteCobro(0);
							}
						}
						dispatch({ type: LOADING_END });
					})
					.catch((error) => {
						if (error.response.status === 500) {
							console.log('error500', error.response.data);
							dispatch({ type: LOADING_END });
						} else if (error.response.status === 400) {
							console.log('error400', error.response.data);
							dispatch({ type: LOADING_END });
						} else if (error.response.status === 403) {
							console.log('error403', error.response.data);
							dispatch({ type: LOADING_END });
						} else if (error.response.status === 404) {
							console.log('error404', error.response.data);
							dispatch({ type: LOADING_END });
						} else {
							console.log(error.response.data);
							dispatch({ type: LOADING_END });
						}
					});
			};
		};
		dispatch<any>(getPendienteCobro());
		//Obtener Ingresos
		const getIngresos = () => {
			return async (dispatch: Dispatch<LoadingDispatchTypes>) => {
				await getToken();
				dispatch({ type: START_LOADING });
				axiosClient
					.get(`/comprobantes/ingresos/`)
					.then((respuesta) => {
						if (respuesta.status === 200) {
							const arrayActuales = respuesta.data.Actual;
							const arrayAnteriores = respuesta.data.Anterior;

							arrayActuales.map((item: any) => {
								if (item.mes === Number(currentMonth)) {
									setIngresosMesActual(item);
								}
							});

							const arrayIngresosActuales = [];
							for (let i = 1; i <= Number(currentMonth); i++) {
								if (arrayActuales.find((item: any) => item.mes === i)) {
									const itemFind = arrayActuales.filter((item: any) => item.mes === i);
									arrayIngresosActuales.push(itemFind[0]);
								} else {
									arrayIngresosActuales.push({ total: 0, mes: i });
								}
							}
							const arrayIngresosAnteriores = [];
							for (let i = 1; i <= 12; i++) {
								if (arrayAnteriores.find((item: any) => item.mes === i)) {
									const itemFind = arrayAnteriores.filter((item: any) => item.mes === i);
									arrayIngresosAnteriores.push(itemFind[0]);
								} else {
									arrayIngresosAnteriores.push({ total: 0, mes: i });
								}
							}
							setIngresosActuales(arrayIngresosActuales);
							setIngresosAnteriores(arrayIngresosAnteriores);
						}
						dispatch({ type: LOADING_END });
					})
					.catch((error) => {
						if (error.response.status === 500) {
							console.log('error500', error.response.data);
							dispatch({ type: LOADING_END });
						} else if (error.response.status === 400) {
							console.log('error400', error.response.data);
							dispatch({ type: LOADING_END });
						} else if (error.response.status === 403) {
							console.log('error403', error.response.data);
							dispatch({ type: LOADING_END });
						} else if (error.response.status === 404) {
							console.log('error404', error.response.data);
							dispatch({ type: LOADING_END });
						} else {
							console.log(error.response.data);
							dispatch({ type: LOADING_END });
						}
					});
			};
		};
		dispatch<any>(getIngresos());
	}, []);

	interface Types {
		labels: string[];
		datasets: {
			label: string;
			color:
				| 'primary'
				| 'secondary'
				| 'info'
				| 'success'
				| 'warning'
				| 'error'
				| 'light'
				| 'dark';
			data: number[];
		}[];
	}

	const actualYearData = ingresosActuales.reduce(
		(acumulador: any, currentValue: any) => {
			return acumulador.concat(currentValue.total);
		},
		[]
	);
	const anteriorYearData = ingresosAnteriores.reduce(
		(acumulador: any, currentValue: any) => {
			return acumulador.concat(currentValue.total);
		},
		[]
	);

	const lineChartData: Types = {
		labels: [
			'Enero',
			'Febrero',
			'Marzo',
			'Abril',
			'Mayo',
			'Junio',
			'Julio',
			'Agosto',
			'Septiembre',
			'Octubre',
			'Noviembre',
			'Diciembre',
		],
		datasets: [
			{
				label: '2022',
				color: 'info',
				data: actualYearData,
			},
			{
				label: '2021',
				color: 'dark',
				data: anteriorYearData,
			},
		],
	};
	return (
		<DashboardLayout>
			<DashboardNavbar />
			<MDBox py={3}>
				<MDBox mt={1.5}>
					<Grid container spacing={3}>
						<Grid item xs={12} md={6} lg={3}>
							<MDBox mb={1.5}>
								<ComplexStatisticsCard
									color='success'
									icon='leaderboard'
									title='Ingresos mes actual'
									count={
										ingresosMesActual.total ? numeroAMoneda(ingresosMesActual.total) : 0
									}
									percentage={{
										color: 'success',
										amount: '',
										label:
											kpis?.facturas_mtd !== undefined
												? `Docs: ${kpis.facturas_mtd} • Neto: ${numeroAMoneda(kpis.subtotal_mtd || 0)} • IVA: ${numeroAMoneda(kpis.iva_mtd || 0)}`
											: `Ingresos en ${numeroAMes(Number(currentMonth))}`,
									}}
								/>
							</MDBox>
						</Grid>
						<Grid item xs={12} md={6} lg={3}>
							<MDBox mb={1.5}>
								<ComplexStatisticsCard
									color='secondary'
									icon='paid'
									title='Cuentas por cobrar '
									count={
										!loading ? (
											`${numeroAMoneda(pendienteCobro)}`
										) : (
											<PXLoader showText={false} />
										)
									}
									percentage={{
										color: 'success',
										amount: '',
										label: 'Monto pendiente de cobro',
									}}
								/>
							</MDBox>
						</Grid>
						<Grid item xs={12} md={6} lg={3}>
							<MDBox mb={1.5}>
								<ComplexStatisticsCard
									color='warning'
									icon='qr_code_2'
									title='Plan contratado'
									count={
										!loading ? (
											suscripcionInfo.plan_nombre || `${suscripcionInfo.plan_id ?? ''}`
										) : (
											<PXLoader showText={false} />
										)
									}
									percentage={{
										color: 'success',
										amount: '',
									label: `Timbres disponibles ${suscripcionInfo.timbres}`,
									}}
								/>
							</MDBox>
						</Grid>
						<Grid item xs={12} md={6} lg={3}>
							<MDBox mb={1.5}>
								<ComplexStatisticsCard
									color='primary'
									icon='today'
									title='Vigencia'
									count={
										!loading ? (
											`${suscripcionInfo.vigencia}`
										) : (
											<PXLoader showText={false} />
										)
									}
									percentage={{
										color: 'success',
										amount: '',
										label: 'Tu plan es vigente hasta',
									}}
								/>
							</MDBox>
						</Grid>
					</Grid>
				</MDBox>

				{/* Alertas operativas */}
				{(kpis?.csd_expiring || (kpis?.cancel_en_proceso_count || 0) > 0) && (
					<MDBox mt={2}>
						<Grid container spacing={2}>
							{(kpis?.csd_expiring || false) && (
								<Grid item xs={12} md={6}>
									<Alert severity='warning' icon={<Icon>verified</Icon>}>
										{`Tu CSD expira el ${kpis?.csd_expiration_date ?? '--'} (${kpis?.csd_days_to_expire ?? '--'} días).`}
										&nbsp;
										<MDButton
											size='small'
											variant='text'
											color='warning'
											onClick={() => (window.location.href = '/configuracion/certificados')}
										>
											Ir a Certificados
										</MDButton>
									</Alert>
								</Grid>
							)}
							{(kpis?.cancel_en_proceso_count || 0) > 0 && (
								<Grid item xs={12} md={6}>
									<Alert severity='error' icon={<Icon>schedule</Icon>}>
										{`${kpis?.cancel_en_proceso_count} cancelaciones en proceso excedieron el tiempo establecido.`} Revisa en Comprobantes.
									</Alert>
								</Grid>
							)}
						</Grid>
					</MDBox>
				)}

				{/* KPIs adicionales */}
				<MDBox mt={3}>
					<Grid container spacing={3}>
						<Grid item xs={12} md={6} lg={3}>
							<MDBox mb={1.5}>
								<ComplexStatisticsCard
									color='info'
									icon='stacked_line_chart'
									title='Variación ingresos MTD'
									count={kpis?.variacion_mtd !== undefined ? formatPct(kpis?.variacion_mtd) : '--'}
									percentage={{ color: 'success', amount: '', label: 'vs mes anterior' }}
								/>
							</MDBox>
						</Grid>
						<Grid item xs={12} md={6} lg={3}>
							<MDBox mb={1.5}>
								<ComplexStatisticsCard
									color='secondary'
									icon='percent'
									title='Tasa de cobro (mes)'
									count={kpis?.tasa_cobro !== undefined ? formatPct(kpis?.tasa_cobro) : '--'}
									percentage={{ color: 'success', amount: '', label: 'pagado / emitido' }}
								/>
							</MDBox>
						</Grid>
						<Grid item xs={12} md={6} lg={3}>
							<MDBox mb={1.5}>
								<ComplexStatisticsCard
									color='dark'
									icon='receipt_long'
									title='Ticket promedio'
									count={kpis?.ticket_promedio ? numeroAMoneda(kpis.ticket_promedio) : '--'}
									percentage={{ color: 'success', amount: '', label: 'promedio por factura (mes)' }}
								/>
							</MDBox>
						</Grid>
						<Grid item xs={12} md={6} lg={3}>
							<MDBox mb={1.5}>
								<ComplexStatisticsCard
									color='error'
									icon='warning'
									title='Facturas vencidas'
									count={kpis?.vencidas_count ?? '--'}
									percentage={{ color: 'error', amount: '', label: 'con pago vencido' }}
								/>
							</MDBox>
						</Grid>
					</Grid>
				</MDBox>

				{/* Acciones rápidas + Búsqueda */}
				<MDBox mt={3}>
					<Grid container spacing={2} alignItems='center'>
						<Grid item>
							<MDButton color='info' variant='contained' onClick={() => (window.location.href = '/comprobantes')}>
								<Icon>add</Icon>&nbsp;Crear factura
							</MDButton>
						</Grid>
						<Grid item>
							<MDButton color='secondary' variant='outlined' onClick={() => (window.location.href = '/pagos')}>
								<Icon>paid</Icon>&nbsp;Registrar pago
							</MDButton>
						</Grid>
						<Grid item xs={12} md={5} lg={6}>
							<TextField
								fullWidth
								label='Buscar por UUID o Folio'
								value={queryDoc}
								onChange={(e) => setQueryDoc(e.target.value)}
								size='small'
							/>
						</Grid>
						<Grid item>
							<Tooltip title='Abrir PDF por UUID'>
								<MDButton
									color='warning'
									onClick={() => {
										if (queryDoc.trim()) window.location.href = `/comprobante/pdf/${queryDoc.trim()}`;
									}}
								>
									<Icon>picture_as_pdf</Icon>&nbsp;Ver PDF
								</MDButton>
							</Tooltip>
						</Grid>
						<Grid item>
							<Tooltip title='Descargar XML por UUID'>
								<MDButton
									color='dark'
									onClick={() => {
										if (queryDoc.trim()) window.location.href = `/comprobante/xml/${queryDoc.trim()}`;
									}}
								>
									<Icon>code</Icon>&nbsp;Descargar XML
								</MDButton>
							</Tooltip>
						</Grid>
					</Grid>
				</MDBox>
				{/* Dashboard Graficas */}
				<MDBox mt={6}>
					<Grid container spacing={3}>
						{/* Grafica Pastel */}
						<Grid item xs={12} md={6} lg={4}>
							<PxcPieChart />
						</Grid>
						{/* Graficas Lineales */}
						<Grid item xs={12} md={6} lg={8}>
							<DefaultLineChart
								title={`Ingresos de ${lastYear} vs ${currentYear}`}
								description={
									<MDBox
										display='flex'
										justifyContent='space-between'
										style={{ minHeight: '70px' }}
									>
										<MDBox display='flex' ml={-1}>
											<MDBadgeDot color='info' size='sm' badgeContent={currentYear} />
											<MDBadgeDot color='dark' size='sm' badgeContent={lastYear} />
										</MDBox>
									</MDBox>
								}
								chart={lineChartData}
							/>
						</Grid>
					</Grid>
				</MDBox>
			</MDBox>
		</DashboardLayout>
	);
}

export default Analytics;
